import { useMemo, useState } from 'react';
import { Button, Form, Input, Select, Typography, message } from 'antd';
import Modal from 'antd/es/modal/Modal';
import { ApiOutlined } from '@ant-design/icons';
import { Authentication } from '@leedpulse-monorepo/classes';
import { UserSubscriptionListItem } from '../../../contexts/forms/users/UserEditionFormContext';
import connectSepteo, { ConnectSepteoPayload } from '../../../../domain/use_cases/users/connectSepteo';

interface ConnectSepteoModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  companies: { id: string; name: string }[];
  subscriptions: UserSubscriptionListItem[];
  leedflowPlanIds: string[];
  auth: Authentication | null;
  onSuccess: () => void;
}

const ConnectSepteoModal: React.FC<ConnectSepteoModalProps> = ({
  open,
  onClose,
  userId,
  companies,
  subscriptions,
  leedflowPlanIds,
  auth,
  onSuccess,
}) => {
  const leedflowSubscriptions = useMemo(
    () => subscriptions.filter((sub) => leedflowPlanIds.includes(sub.idPlan)),
    [subscriptions, leedflowPlanIds]
  );

  const [companyId, setCompanyId] = useState<string | undefined>(companies.length === 1 ? companies[0].id : undefined);
  const [subscriptionId, setSubscriptionId] = useState<string | undefined>(
    leedflowSubscriptions.length === 1 ? leedflowSubscriptions[0].id : undefined
  );
  const [partnerApiKey, setPartnerApiKey] = useState('');
  const [partnerUid, setPartnerUid] = useState('');
  const [partnerUidUser, setPartnerUidUser] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  const handleSubmit = async () => {
    if (!auth) return;
    if (!companyId || !subscriptionId || !partnerApiKey.trim() || !partnerUid.trim() || !partnerUidUser.trim()) {
      message.error('Veuillez renseigner tous les champs.');
      return;
    }
    setIsBusy(true);
    try {
      await connectSepteo(
        new ConnectSepteoPayload(
          userId,
          companyId,
          subscriptionId,
          partnerApiKey.trim(),
          partnerUid.trim(),
          partnerUidUser.trim()
        ),
        auth
      );
      message.success('Instance Septeo connectée. Synchronisation lancée.');
      onSuccess();
      onClose();
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Erreur lors de la connexion Septeo.');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ApiOutlined size={20} style={{ marginRight: 6 }} />
          <span style={{ fontSize: '16px', fontWeight: 600 }}>Connecter Septeo</span>
        </div>
      }
      centered
      open={open}
      onCancel={onClose}
      okText="Connecter et synchroniser"
      cancelText="Annuler"
      confirmLoading={isBusy}
      onOk={handleSubmit}
      width={500}
    >
      <Form layout="vertical" style={{ marginTop: '2rem' }}>
        {companies.length > 1 && (
          <Form.Item label="Entreprise">
            <Select
              value={companyId}
              onChange={setCompanyId}
              placeholder="Sélectionner l'entreprise"
              options={companies.map((c) => ({ label: c.name, value: c.id }))}
            />
          </Form.Item>
        )}
        {leedflowSubscriptions.length > 1 && (
          <Form.Item label="Abonnement Leedflow">
            <Select
              value={subscriptionId}
              onChange={setSubscriptionId}
              placeholder="Sélectionner l'abonnement"
              options={leedflowSubscriptions.map((s) => ({ label: s.name, value: s.id }))}
            />
          </Form.Item>
        )}
        <Form.Item label="Clé API Septeo (partner_api_key)">
          <Input value={partnerApiKey} onChange={(e) => setPartnerApiKey(e.target.value)} />
        </Form.Item>
        <Form.Item label="UID agence Septeo (partner_uid)">
          <Input value={partnerUid} onChange={(e) => setPartnerUid(e.target.value)} />
        </Form.Item>
        <Form.Item label="UID utilisateur Septeo (partner_uid)">
          <Input value={partnerUidUser} onChange={(e) => setPartnerUidUser(e.target.value)} />
        </Form.Item>
        <Typography.Text type="secondary" style={{ fontSize: '13px' }}>
          Connecte l'entreprise et l'utilisateur à Septeo puis lance une synchronisation complète des contacts.
        </Typography.Text>
      </Form>
    </Modal>
  );
};

export default ConnectSepteoModal;
